<template>
  <div class="attendance-panel">
    <!-- Stat Widgets -->
    <div class="stat-grid">
      <div
        class="stat-card stat-card-blue stat-card-clickable"
        :class="{ 'stat-card-active': activeWidget === 'clocked_in' }"
        @click="toggleWidget('clocked_in')"
      >
        <div class="stat-icon"><b-icon-people-fill /></div>
        <div class="stat-body">
          <span class="stat-value">{{ clockedIn.length }}</span>
          <span class="stat-label">Clocked In</span>
          <span class="stat-hint"
            >{{ onSite.length }} GPS-verified on site</span
          >
        </div>
      </div>

      <div
        class="stat-card stat-card-amber stat-card-clickable"
        :class="{ 'stat-card-active': activeWidget === 'awaiting' }"
        @click="toggleWidget('awaiting')"
      >
        <div class="stat-icon"><b-icon-clock-history /></div>
        <div class="stat-body">
          <span class="stat-value">{{ awaitingSignOff.length }}</span>
          <span class="stat-label">Awaiting Sign-Off</span>
          <span class="stat-hint">clocked off, pending approval</span>
        </div>
      </div>

      <div
        class="stat-card stat-card-green stat-card-clickable"
        :class="{ 'stat-card-active': activeWidget === 'approved' }"
        @click="toggleWidget('approved')"
      >
        <div class="stat-icon"><b-icon-check-circle-fill /></div>
        <div class="stat-body">
          <span class="stat-value">{{ totalHoursToday }}h</span>
          <span class="stat-label">Approved Hours Today</span>
          <span class="stat-hint">approved hours only</span>
        </div>
      </div>

      <div
        class="stat-card stat-card-purple stat-card-clickable"
        :class="{ 'stat-card-active': activeWidget === 'payperiod' }"
        @click="toggleWidget('payperiod')"
      >
        <div class="stat-icon"><b-icon-calendar-range /></div>
        <div class="stat-body">
          <span class="stat-value">{{ payPeriodTotalHours }}h</span>
          <span class="stat-label">Pay Period Hours</span>
          <span class="stat-hint">{{ payPeriodLabel }}</span>
        </div>
      </div>
    </div>

    <!-- Events / Detail Card -->
    <div class="events-card">
      <div class="events-card-header">
        <h3 v-if="activeWidget === 'payperiod'">
          Pay Period Hours
          <span v-if="payPeriodLabel" class="date-highlight">{{
            payPeriodLabel
          }}</span>
        </h3>
        <h3 v-else>{{ panelTitle }}</h3>
        <span v-if="activeWidget !== 'payperiod'" class="events-count">
          {{ visibleEvents.length }} entries
        </span>
        <button
          v-if="activeWidget"
          class="clear-filter-btn"
          @click="activeWidget = null"
        >
          Show all
        </button>
      </div>

      <!-- Pay Period Controls -->
      <div v-if="activeWidget === 'payperiod'" class="pay-period-controls">
        <div class="pp-field">
          <label>Project</label>
          <b-form-select
            v-model="payPeriod.projectId"
            :options="projectOptions"
            size="sm"
            style="min-width: 180px"
            @change="loadPayPeriod"
          />
        </div>
        <template v-if="usePayPeriodTimeframe">
          <div class="pp-field">
            <label>Pay Period</label>
            <div class="pp-period-nav">
              <button
                type="button"
                class="pp-nav-btn"
                :disabled="payPeriod.offset <= -52 || payPeriod.loading"
                @click="changePayPeriod(-1)"
              >
                <b-icon-chevron-left />
              </button>
              <span class="pp-period-label date-highlight">{{
                payPeriodLabel
              }}</span>
              <button
                type="button"
                class="pp-nav-btn"
                :disabled="payPeriod.offset === 0 || payPeriod.loading"
                @click="changePayPeriod(1)"
              >
                <b-icon-chevron-right />
              </button>
            </div>
          </div>
          <div class="pp-field pp-field-action">
            <b-button
              size="sm"
              variant="outline-secondary"
              @click="setCustomRange(true)"
            >
              Custom range
            </b-button>
          </div>
        </template>
        <template v-else>
          <div class="pp-field">
            <label>From</label>
            <b-form-datepicker
              v-model="payPeriod.dateFrom"
              locale="en-AU"
              :date-format-options="dateFormatOptions"
              size="sm"
              today-button
              reset-button
              close-button
              show-decade-nav
              style="width: 160px"
              @input="loadPayPeriod"
            />
          </div>
          <div class="pp-field">
            <label>To</label>
            <b-form-datepicker
              v-model="payPeriod.dateTo"
              locale="en-AU"
              :date-format-options="dateFormatOptions"
              size="sm"
              today-button
              reset-button
              close-button
              show-decade-nav
              style="width: 160px"
              @input="loadPayPeriod"
            />
          </div>
          <div v-if="payPeriodModeAvailable" class="pp-field pp-field-action">
            <b-button
              size="sm"
              variant="outline-secondary"
              @click="setCustomRange(false)"
            >
              Use pay period
            </b-button>
          </div>
        </template>
      </div>

      <!-- Pay Period Table -->
      <template v-if="activeWidget === 'payperiod'">
        <div v-if="payPeriod.loading" class="events-empty"><p>Loading…</p></div>
        <div v-else-if="payPeriodRows.length === 0" class="events-empty">
          <b-icon-clipboard class="empty-icon" />
          <p>No hours found for this period.</p>
        </div>
        <template v-else>
          <div class="pp-table-row pp-table-header">
            <span>Tradie</span><span>Approved</span><span>Pending</span
            ><span>Total</span>
          </div>
          <div
            v-for="row in payPeriodRows"
            :key="row.tradieId"
            class="pp-table-row"
          >
            <span>{{ row.tradieName }}</span>
            <span class="hours-approved">{{ row.approvedHours }}h</span>
            <span class="hours-pending">{{ row.pendingHours }}h</span>
            <span class="hours-total">{{ row.totalHours }}h</span>
          </div>
        </template>
      </template>

      <!-- Events Table -->
      <template v-else>
        <div v-if="visibleEvents.length === 0" class="events-empty">
          <b-icon-clipboard class="empty-icon" />
          <p>No timesheet events for this date.</p>
        </div>

        <div v-else class="project-list">
          <div
            v-for="project in groupedByProject"
            :key="project.project_id"
            class="project-card"
          >
            <div class="project-card-header">
              <div>
                <h3 class="project-name">{{ project.project_name }}</h3>
                <p class="project-meta">
                  {{ project.events.length }}
                  {{ project.events.length === 1 ? 'entry' : 'entries' }}
                </p>
              </div>
              <span v-if="project.onSiteCount" class="project-badge">
                {{ project.onSiteCount }} on site
              </span>
            </div>

            <div class="table-wrap">
              <!-- stacked="lg": below 992px each entry renders as a compact
                   label/value card — never a horizontally scrolling table.
                   From 992px up the full table fits without overflow. -->
              <b-table
                :items="project.events"
                :fields="fields"
                hover
                stacked="lg"
                class="events-table mb-0"
              >
                <template #cell(tradie_name)="{ item }">
                  <router-link
                    v-if="item.tradie && item.tradie.id"
                    :to="{
                      name: 'tradies.view-tradie',
                      params: { tradieId: String(item.tradie.id) },
                    }"
                    class="worker-name-link"
                    >{{ item.tradie.name }}</router-link
                  >
                  <span v-else class="worker-name">-</span>
                </template>
                <template #cell(date)="{ item }">{{
                  formatDate(item.clocked_in_at, item.timezone)
                }}</template>
                <template #cell(zone_name)="{ item }">{{
                  zoneDisplayName(item.zone)
                }}</template>
                <template #cell(time)="{ item }">
                  <span class="time-range">
                    <span>{{
                      formatTime(item.clocked_in_at, item.timezone)
                    }}</span>
                    <span class="time-sep">–</span>
                    <span>{{
                      formatTime(item.clocked_out_at, item.timezone)
                    }}</span>
                    <span
                      v-if="timezoneLabel(item.timezone)"
                      class="text-muted small ml-1"
                      >{{ timezoneLabel(item.timezone) }}</span
                    >
                  </span>
                </template>
                <template #cell(break_minutes)="{ item }">{{
                  item.break_minutes ? item.break_minutes + 'm' : '-'
                }}</template>
                <template #cell(hours)="{ item }">{{
                  item.clocked_in_at && item.clocked_out_at
                    ? calcHours(item) + 'h'
                    : '-'
                }}</template>
                <template #cell(status)="{ item }">
                  <span class="status-pill" :class="eventStatusClass(item)">{{
                    eventStatusLabel(item)
                  }}</span>
                </template>
                <template #cell(actions)="{ item }">
                  <div class="action-group">
                    <b-button
                      size="sm"
                      variant="outline-secondary"
                      @click="$refs.entryDetailModal.open(item)"
                      >View</b-button
                    >
                    <b-button-group
                      v-if="canApprove && isApprovableEvent(item)"
                      size="sm"
                    >
                      <b-button
                        variant="outline-success"
                        @click="$emit('approve', item.id)"
                        >Approve</b-button
                      >
                      <b-button
                        variant="outline-danger"
                        @click="$emit('reject', item.id)"
                        >Reject</b-button
                      >
                    </b-button-group>
                  </div>
                </template>
              </b-table>
            </div>
          </div>
        </div>
      </template>
    </div>

    <timesheet-entry-detail-modal
      ref="entryDetailModal"
      modal-id="dashboard-entry-detail-modal"
      :can-approve="canApprove"
      :projects="projects"
      @approve="onDetailApprove"
      @reject="onDetailReject"
    />
  </div>
</template>

<script>
import dayjs from 'dayjs'
import Api from '@/api'
import {
  zoneDisplayName,
  projectNameFromEvent,
  isOnSite,
  isClockedIn,
  isApprovableEvent,
  eventStatusLabel,
  eventStatusClass,
  formatBusinessTime,
  formatBusinessDateTime,
  timezoneLabel,
  eventMinutes,
} from '@/utils/timesheetHelpers'
import TimesheetEntryDetailModal from '@/components/timesheets/TimesheetEntryDetailModal.vue'

export default {
  name: 'DashboardAttendancePanel',
  components: { TimesheetEntryDetailModal },
  props: {
    events: {
      type: Array,
      default: function () {
        return []
      },
    },
    canApprove: { type: Boolean, default: false },
    eventsTitle: { type: String, default: "Today's Events" },
  },
  data() {
    return {
      activeWidget: null,
      payPeriodLoaded: false,
      // en-AU default renders "29 August 2026"; force numeric dd/mm/yyyy.
      dateFormatOptions: { year: 'numeric', month: '2-digit', day: '2-digit' },
      payPeriod: {
        projectId: null,
        dateFrom: dayjs().startOf('isoWeek').format('YYYY-MM-DD'),
        dateTo: dayjs().format('YYYY-MM-DD'),
        offset: 0,
        custom: false,
        meta: null,
        loading: false,
        events: [],
      },
    }
  },
  computed: {
    fields() {
      return [
        { key: 'tradie_name', label: 'Worker' },
        { key: 'date', label: 'Date', thStyle: 'width: 110px' },
        { key: 'zone_name', label: 'Zone', thStyle: 'width: 110px' },
        { key: 'time', label: 'Time', thStyle: 'width: 160px' },
        { key: 'break_minutes', label: 'Break', thStyle: 'width: 70px' },
        { key: 'hours', label: 'Hours', thStyle: 'width: 70px' },
        { key: 'status', label: 'Status', thStyle: 'width: 110px' },
        {
          key: 'actions',
          label: '',
          thStyle: 'width: 170px',
          tdClass: 'actions-cell',
        },
      ]
    },
    projectOptions() {
      return this.$store.getters['timesheet/projectOptions']
    },
    projects() {
      return this.$store.state.project.projects || []
    },
    timesheetSettings() {
      return this.$store.getters['timesheet/timesheetSettings']
    },
    // Only truthy once /timesheet-settings has loaded. While loading (null)
    // or on an old API (false) the widget keeps the legacy From/To behaviour
    // — sending timeframe params to an old API would return unfiltered events.
    payPeriodModeAvailable() {
      return !!this.timesheetSettings
    },
    usePayPeriodTimeframe() {
      return this.payPeriodModeAvailable && !this.payPeriod.custom
    },
    clockedIn() {
      return this.events.filter(isClockedIn)
    },
    onSite() {
      return this.events.filter(isOnSite)
    },
    awaitingSignOff() {
      return this.events.filter(function (e) {
        return e.status === 'clocked_out'
      })
    },
    approvedEvents() {
      return this.events.filter(function (e) {
        return e.status === 'approved'
      })
    },
    totalHoursToday() {
      var totalMins = this.approvedEvents
        .filter(function (e) {
          return e.clocked_in_at && e.clocked_out_at
        })
        .reduce(function (sum, e) {
          return (
            sum +
            eventMinutes(e.clocked_in_at, e.clocked_out_at, e.break_minutes)
          )
        }, 0)
      return (totalMins / 60).toFixed(1)
    },
    visibleEvents() {
      if (this.activeWidget === 'clocked_in') return this.clockedIn
      if (this.activeWidget === 'awaiting') return this.awaitingSignOff
      if (this.activeWidget === 'approved') return this.approvedEvents
      return this.events
    },
    panelTitle() {
      if (this.activeWidget === 'clocked_in') return 'Currently Clocked In'
      if (this.activeWidget === 'awaiting') return 'Awaiting Sign-Off'
      if (this.activeWidget === 'approved') return 'Approved Today'
      if (this.activeWidget === 'payperiod') {
        return this.payPeriodLabel
          ? 'Pay Period Hours · ' + this.payPeriodLabel
          : 'Pay Period Hours'
      }
      return this.eventsTitle
    },
    groupedByProject() {
      var projectMap = {}
      var projects = this.projects
      this.visibleEvents.forEach(function (e) {
        var projectId = e.project_id || 'unassigned'
        var projectName = projectNameFromEvent(e, projects)
        if (!projectMap[projectId]) {
          projectMap[projectId] = {
            project_id: projectId,
            project_name: projectName,
            events: [],
            onSiteCount: 0,
          }
        }
        projectMap[projectId].events.push(e)
        if (isOnSite(e)) projectMap[projectId].onSiteCount += 1
      })
      return Object.values(projectMap).sort(function (a, b) {
        return a.project_name.localeCompare(b.project_name)
      })
    },
    payPeriodRows() {
      var map = {}
      this.payPeriod.events.forEach(function (e) {
        var tid = (e.tradie && e.tradie.id) || 'unknown'
        if (!map[tid]) {
          map[tid] = {
            tradieId: tid,
            tradieName: (e.tradie && e.tradie.name) || 'Unknown',
            approvedMins: 0,
            pendingMins: 0,
            totalMins: 0,
          }
        }
        if (e.clocked_in_at && e.clocked_out_at) {
          var mins = eventMinutes(
            e.clocked_in_at,
            e.clocked_out_at,
            e.break_minutes,
          )
          map[tid].totalMins += mins
          if (e.status === 'approved') map[tid].approvedMins += mins
          else if (e.status === 'clocked_out') map[tid].pendingMins += mins
        }
      })
      return Object.values(map)
        .map(function (r) {
          return {
            tradieId: r.tradieId,
            tradieName: r.tradieName,
            approvedHours: (r.approvedMins / 60).toFixed(1),
            pendingHours: (r.pendingMins / 60).toFixed(1),
            totalHours: (r.totalMins / 60).toFixed(1),
          }
        })
        .sort(function (a, b) {
          return a.tradieName.localeCompare(b.tradieName)
        })
    },
    payPeriodTotalHours() {
      var total = 0
      this.payPeriod.events.forEach(function (e) {
        if (e.clocked_in_at && e.clocked_out_at && e.status === 'approved') {
          total += eventMinutes(
            e.clocked_in_at,
            e.clocked_out_at,
            e.break_minutes,
          )
        }
      })
      return (total / 60).toFixed(1)
    },
    payPeriodLabel() {
      if (this.usePayPeriodTimeframe) {
        var p = this.payPeriod.meta || this.periodFromSettings()
        if (p) {
          return (
            dayjs(p.start_date).format('D MMM') +
            ' – ' +
            dayjs(p.end_date).format('D MMM')
          )
        }
        return 'current pay period'
      }
      return (
        dayjs(this.payPeriod.dateFrom).format('D MMM') +
        ' – ' +
        dayjs(this.payPeriod.dateTo).format('D MMM')
      )
    },
  },
  watch: {
    activeWidget(val) {
      if (val === 'payperiod' && !this.payPeriodLoaded) {
        this.loadPayPeriod()
      }
    },
    // Default the pay-period project filter to the first available project once
    // options load (scoped users have no "All Projects" entry).
    projectOptions: {
      immediate: true,
      handler(options) {
        if (this.payPeriod.projectId != null) return
        if (options.some((o) => o.value == null && !o.disabled)) return
        const first = options.find((o) => o.value != null && !o.disabled)
        if (!first) return
        this.payPeriod.projectId = first.value
        // Re-pull only if it was already loaded; otherwise the existing
        // load flow will use this selection when it first runs.
        if (this.payPeriodLoaded) this.loadPayPeriod()
      },
    },
  },
  created() {},
  methods: {
    zoneDisplayName,
    timezoneLabel,
    isOnSite,
    isClockedIn,
    isApprovableEvent,
    eventStatusLabel,
    eventStatusClass,
    toggleWidget(name) {
      this.activeWidget = this.activeWidget === name ? null : name
    },
    onDetailApprove(eventId) {
      this.$emit('approve', eventId)
    },
    onDetailReject(payload) {
      this.$emit('reject-with-reason', payload)
    },
    formatTime(iso, tz) {
      return iso ? formatBusinessTime(iso, undefined, tz) : '-'
    },
    formatDate(iso, tz) {
      return iso ? formatBusinessDateTime(iso, 'ddd D MMM', tz) : '-'
    },
    calcHours(event) {
      var mins = eventMinutes(
        event.clocked_in_at,
        event.clocked_out_at,
        event.break_minutes,
      )
      return (mins / 60).toFixed(1)
    },
    periodFromSettings() {
      var s = this.timesheetSettings
      if (!s) return null
      var offset = this.payPeriod.offset
      if (Array.isArray(s.periods)) {
        var match = s.periods.find(function (p) {
          return p.offset === offset
        })
        if (match) return match
      }
      if (offset === 0 && s.current_period) return s.current_period
      return null
    },
    changePayPeriod(dir) {
      var next = this.payPeriod.offset + dir
      if (next > 0 || next < -52) return
      this.payPeriod.offset = next
      // Keep the previous label (instead of blanking it) until
      // loadPayPeriod() resolves the real range from the live API response,
      // which works for any offset back to -52 — the pre-computed settings
      // array only covers a handful of recent periods.
      this.loadPayPeriod()
    },
    setCustomRange(custom) {
      this.payPeriod.custom = custom
      this.loadPayPeriod()
    },
    async loadPayPeriod() {
      this.payPeriod.loading = true
      this.payPeriodLoaded = true
      try {
        var allEvents = []
        var page = 1
        var lastPage = 1
        do {
          var params = { per_page: 100, page: page }
          if (this.usePayPeriodTimeframe) {
            params.timeframe = 'pay_period'
            params.period_offset = this.payPeriod.offset
          } else {
            params.date_from = this.payPeriod.dateFrom
            params.date_to = this.payPeriod.dateTo
          }
          if (this.payPeriod.projectId)
            params.project_id = this.payPeriod.projectId
          var res = await Api.get('/timesheet-events', { params })
          allEvents = allEvents.concat(res.data.events || [])
          if (res.data.meta && res.data.meta.period) {
            this.payPeriod.meta = res.data.meta.period
          }
          lastPage =
            (res.data.meta && res.data.meta.last_page) ||
            res.data.last_page ||
            1
          page++
        } while (page <= lastPage)
        this.payPeriod.events = allEvents
      } catch (e) {
        this.$bvToast.toast('Failed to load pay period data.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.payPeriod.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/layouts/timesheet-shared';

.stat-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
}

.stat-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  border-left-width: 4px;
}

.stat-card-clickable {
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s;
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

.stat-card-active {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12) !important;
}

.stat-card-blue {
  border-left-color: #3b82f6;
  .stat-icon {
    color: #1d4ed8;
    background: #eff6ff;
  }
  &.stat-card-active {
    border-color: #3b82f6;
  }
}

.stat-card-amber {
  border-left-color: #f59e0b;
  .stat-icon {
    color: #b45309;
    background: #fef3c7;
  }
  &.stat-card-active {
    border-color: #f59e0b;
  }
}

.stat-card-green {
  border-left-color: #22c55e;
  .stat-icon {
    color: #15803d;
    background: #dcfce7;
  }
  &.stat-card-active {
    border-color: #22c55e;
  }
}

.stat-card-purple {
  border-left-color: #8b5cf6;
  .stat-icon {
    color: #6d28d9;
    background: #ede9fe;
  }
  &.stat-card-active {
    border-color: #8b5cf6;
  }
}

.stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  font-size: 18px;
  flex-shrink: 0;
}

.stat-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  line-height: 1.1;
}

.stat-label {
  margin-top: 2px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.stat-hint {
  margin-top: 2px;
  font-size: 11px;
  color: #9ca3af;
}

.events-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.events-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid #f3f4f6;
  background: #fafafa;

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #111827;
    flex: 1;
  }
}

.events-count {
  font-size: 12px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 3px 10px;
  border-radius: 20px;
}

.clear-filter-btn {
  font-size: 12px;
  color: #6b7280;
  background: none;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 3px 10px;
  cursor: pointer;
  &:hover {
    background: #f3f4f6;
  }
}

.events-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
  color: #9ca3af;

  p {
    margin: 12px 0 0;
    font-size: 13px;
  }
}

.empty-icon {
  font-size: 2rem;
  color: #d1d5db;
}

/* Pay Period */
.pay-period-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 14px 16px;
  border-bottom: 1px solid #f3f4f6;
}

.pp-field {
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: 0;
  }
}

.pp-field-action {
  justify-content: flex-end;
}

.pp-period-nav {
  display: flex;
  align-items: center;
  gap: 6px;
}

.pp-nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #374151;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
}

.pp-period-label {
  min-width: 130px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}

.date-highlight {
  display: inline-block;
  margin-left: 6px;
  padding: 2px 10px;
  border-radius: 20px;
  background: #dcfce7;
  color: #166534;
  font-size: 12px;
  font-weight: 600;
}

.pp-table-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  padding: 8px 16px;
  font-size: 13px;
  color: #374151;

  &:nth-child(even) {
    background: #f9fafb;
  }

  &.pp-table-header {
    font-weight: 600;
    color: #6b7280;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    background: transparent !important;
    padding-top: 12px;
    padding-bottom: 6px;
  }
}

/* Phones: the 4-across grid squashes names into a sliver — give the tradie
   name its own line with the three hour figures aligned beneath it. */
@media (max-width: 575.98px) {
  .pp-table-row {
    grid-template-columns: repeat(3, 1fr);
    row-gap: 2px;
  }

  .pp-table-row > span:first-child {
    grid-column: 1 / -1;
    font-weight: 600;
  }

  /* Header: drop the "Tradie" cell so the three hour labels line up with
     the figures below. */
  .pp-table-row.pp-table-header > span:first-child {
    display: none;
  }
}

.hours-approved {
  color: #16a34a;
  font-weight: 600;
}
.hours-pending {
  color: #ca8a04;
  font-weight: 600;
}
.hours-total {
  color: #111827;
  font-weight: 700;
}

/* Events Table — no horizontal scrolling at any width: below 992px the rows
   render stacked (see stacked="lg"), above it the table fits and long values
   wrap within their cells instead of widening the table. */
.table-wrap {
  overflow-x: visible;
}

.project-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;

  /* Phones: the nested paddings (page → panel → project card → row) eat a
     narrow screen — slim them down so the values get the width instead. */
  @media (max-width: 575.98px) {
    gap: 12px;
    padding: 10px;
  }
}

.project-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.project-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  background: #fafafa;
  border-bottom: 1px solid #f3f4f6;
}

.project-name {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}

.project-meta {
  margin: 2px 0 0;
  font-size: 12px;
  color: #6b7280;
}

.project-badge {
  font-size: 12px;
  font-weight: 600;
  color: #1d4ed8;
  background: #eff6ff;
  padding: 4px 10px;
  border-radius: 20px;
  white-space: nowrap;
}

.worker-name {
  font-weight: 500;
  color: #111827;
}

.worker-name-link {
  font-weight: 500;
  color: #1d4ed8;
  text-decoration: none;
  &:hover {
    color: #1e40af;
    text-decoration: underline;
  }
}

.time-range {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #374151;
  white-space: nowrap;
}

.time-sep {
  color: #9ca3af;
}

.action-group {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
  white-space: nowrap;
}

/deep/ .events-table {
  @include timesheet-table-headers;
}

/* Phones/tablets: the events table renders stacked (see stacked="lg" in the
   template). Tighten bootstrap-vue's default stacked look into compact
   cards — small uppercase labels in a fixed column, values beside them — so
   entries stay readable all the way down to 320px with no sideways scroll. */
@media (max-width: 991.98px) {
  .events-card-header {
    flex-wrap: wrap;
    row-gap: 6px;

    /* Title takes its own line; the count pill + "Show all" sit below it
       instead of squeezing the title into a sliver. */
    h3 {
      flex: 1 1 100%;
    }
  }

  /* Selector carries all four classes on purpose: bootstrap-vue's own stacked
     rules (.table.b-table.b-table-stacked-lg > tbody > tr > [data-label])
     set a 40% right-aligned label column, and anything weaker loses to them —
     which left the cards hollow, labels floating mid-card. */
  /deep/ .events-table.table.b-table.b-table-stacked-lg {
    > tbody > tr {
      display: block;
      padding: 12px 14px;
      border-bottom: 1px solid #f3f4f6;

      &:last-child {
        border-bottom: 0;
      }
    }

    /* Between phone and the full table, a single column leaves the right
       half of the card empty — flow the fields into two columns instead.
       The worker name and the actions row keep the full width. */
    @media (min-width: 576px) {
      > tbody > tr {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        column-gap: 40px;
        align-items: baseline;
      }

      > tbody > tr > td[data-label='Worker'],
      > tbody > tr > td.actions-cell {
        grid-column: 1 / -1;
      }
    }

    /* Each field: two aligned columns — labels in a fixed left column, all
       values starting at the same x, left-aligned. Labels never wrap; values
       wrap only at word boundaries — no mid-word breaking at any width. */
    > tbody > tr > td {
      display: grid;
      grid-template-columns: 110px minmax(0, 1fr);
      align-items: baseline;
      gap: 12px;
      padding: 3px 0;
      border: 0;
      font-size: 13px;

      &::before {
        content: attr(data-label);
        float: none;
        width: auto;
        margin: 0;
        padding: 0;
        text-align: left;
        white-space: nowrap;
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #9ca3af;
      }

      > div {
        width: auto;
        min-width: 0;
        margin: 0;
        padding: 0;
        text-align: left;
        word-break: normal;
        overflow-wrap: normal;
        color: #374151;
      }
    }

    /* Worker name is the card's title: full width, larger, no label. */
    > tbody > tr > td[data-label='Worker'] {
      display: block;
      padding-bottom: 6px;

      &::before {
        display: none;
      }

      > div {
        text-align: left;
        font-size: 15px;
        font-weight: 600;
      }
    }

    /* The actions column has no label — let the buttons/status span the full
       card width. */
    > tbody > tr > td.actions-cell {
      display: block;
      margin-top: 6px;

      &::before {
        display: none;
      }

      > div {
        text-align: left;
      }

      .action-group {
        justify-content: flex-start;
      }
    }
  }
}
</style>
